import React, { useEffect, useRef, useState } from "react";
import axios from "axios";
import { MessageCircle, X, Send, LifeBuoy, Sparkles, Mail } from "lucide-react";
import { useApp, API } from "../AppContext";

/**
 * Floating support bubble (bottom-right). Two modes:
 *  - "Assistant IA": chat with PROGÈRE AI (uses /api/ai/chat, dedicated session)
 *  - "Contacter": open a support ticket (POST /api/support/ticket, mailto fallback)
 */
const newSession = () => `support-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;

export default function SupportBubble() {
  const { lang, user } = useApp();
  const [open, setOpen] = useState(false);
  const [tab, setTab] = useState("ai"); // 'ai' | 'ticket'
  const isFr = lang === "fr";

  // --- AI chat state ---
  const [sessionId] = useState(newSession);
  const [messages, setMessages] = useState([]);
  const [aiInput, setAiInput] = useState("");
  const [aiLoading, setAiLoading] = useState(false);
  const scrollRef = useRef(null);
  useEffect(() => { scrollRef.current?.scrollTo({ top: 999999, behavior: "smooth" }); }, [messages, aiLoading]);

  const sendAI = async () => {
    if (!aiInput.trim()) return;
    const msg = aiInput.trim();
    setAiInput("");
    setMessages((m) => [...m, { role: "user", content: msg, id: `u-${Date.now()}` }]);
    setAiLoading(true);
    try {
      const { data } = await axios.post(`${API}/ai/chat`, { session_id: sessionId, message: msg, language: lang });
      setMessages((m) => [...m, { role: "assistant", content: data.reply, id: `a-${Date.now()}` }]);
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", content: (isFr ? "Erreur : " : "Error: ") + (e?.response?.data?.detail || e.message), id: `e-${Date.now()}` }]);
    } finally { setAiLoading(false); }
  };

  // --- Support ticket state ---
  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [ticketSent, setTicketSent] = useState(false);
  const [ticketErr, setTicketErr] = useState("");
  const [sending, setSending] = useState(false);

  const submitTicket = async (e) => {
    e.preventDefault();
    setTicketErr(""); setSending(true);
    try {
      await axios.post(`${API}/support/ticket`, {
        subject: subject || (isFr ? "Demande de support" : "Support request"),
        message,
      });
      setTicketSent(true);
      setSubject(""); setMessage("");
    } catch (e) {
      // Fallback: mailto
      const body = encodeURIComponent(`${message}\n\n---\nPROGÈRE Devis AI · ${user?.email || ""}`);
      const sub = encodeURIComponent(subject || (isFr ? "Demande de support" : "Support request"));
      window.location.href = `mailto:support@progere.ca?subject=${sub}&body=${body}`;
      setTicketErr(e?.response?.data?.detail || (isFr ? "Envoi par courriel..." : "Sending via email..."));
    } finally { setSending(false); }
  };

  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)}
          className="fixed bottom-5 right-5 w-14 h-14 rounded-full flex items-center justify-center z-[60] hover:scale-105 transition"
          style={{
            background: "linear-gradient(180deg, #F5D062 0%, #D4AF37 100%)",
            boxShadow: "0 12px 32px rgba(212,175,55,0.5), inset 0 1px 0 rgba(255,255,255,0.4)",
            border: "1px solid rgba(212,175,55,0.5)",
          }}
          aria-label="Support" data-testid="support-bubble">
          <MessageCircle size={22} color="#0B0B0B" />
        </button>
      )}
      {open && (
        <div className="fixed bottom-5 right-5 w-[360px] max-w-[calc(100vw-2.5rem)] z-[60] rounded-2xl overflow-hidden flex flex-col"
          style={{
            background: "linear-gradient(180deg, rgba(24,24,24,0.98), rgba(11,11,11,0.98))",
            border: "1px solid rgba(212,175,55,0.25)",
            boxShadow: "0 20px 60px rgba(0,0,0,0.8)",
            maxHeight: "540px",
          }}
          data-testid="support-panel">
          <div className="flex items-center justify-between px-4 py-3 border-b" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
            <div className="flex items-center gap-2">
              <LifeBuoy size={16} color="#D4AF37" />
              <span className="font-heading text-sm font-bold" style={{ color: "#F5F5F5" }}>
                {isFr ? "Support PROGÈRE" : "PROGÈRE Support"}
              </span>
            </div>
            <button onClick={() => setOpen(false)} className="p-1 rounded hover:bg-white/5" aria-label="Close" data-testid="support-close">
              <X size={16} color="rgba(245,245,245,0.7)" />
            </button>
          </div>

          {/* Tabs */}
          <div className="flex px-2 pt-2 gap-1">
            <TabBtn active={tab === "ai"} onClick={() => setTab("ai")} testid="support-tab-ai" icon={<Sparkles size={12} />}>{isFr ? "Assistant IA" : "AI Assistant"}</TabBtn>
            <TabBtn active={tab === "ticket"} onClick={() => setTab("ticket")} testid="support-tab-ticket" icon={<Mail size={12} />}>{isFr ? "Contacter" : "Contact"}</TabBtn>
          </div>

          {tab === "ai" && (
            <div className="flex flex-col flex-1 min-h-0">
              <div ref={scrollRef} className="flex-1 overflow-y-auto p-3 space-y-2 min-h-[220px]">
                {messages.length === 0 && (
                  <div className="text-xs px-2 py-6 text-center" style={{ color: "rgba(245,245,245,0.55)" }}>
                    {isFr
                      ? "Bonjour ! Je suis PROGÈRE AI. Posez votre question — je peux vous guider dans l'app, expliquer une fonctionnalité ou aider avec une soumission."
                      : "Hi! I'm PROGÈRE AI. Ask a question — I can guide you in the app, explain a feature or help with a quote."}
                  </div>
                )}
                {messages.map((m) => (
                  <div key={m.id} className={`flex ${m.role === "user" ? "justify-end" : "justify-start"}`}>
                    <div className="max-w-[85%] px-3 py-2 rounded-2xl text-xs whitespace-pre-wrap"
                      style={m.role === "user"
                        ? { background: "linear-gradient(180deg, #C00000, #A00000)", color: "#F5F5F5" }
                        : { background: "rgba(212,175,55,0.08)", color: "rgba(245,245,245,0.92)", border: "1px solid rgba(212,175,55,0.18)" }}>
                      {m.content}
                    </div>
                  </div>
                ))}
                {aiLoading && (
                  <div className="flex justify-start">
                    <div className="px-3 py-2 rounded-2xl text-xs" style={{ background: "rgba(212,175,55,0.08)", border: "1px solid rgba(212,175,55,0.18)", color: "rgba(245,245,245,0.7)" }}>
                      {isFr ? "PROGÈRE AI réfléchit…" : "PROGÈRE AI thinking…"}
                    </div>
                  </div>
                )}
              </div>
              <div className="p-3 border-t flex gap-2 items-end" style={{ borderColor: "rgba(212,175,55,0.15)" }}>
                <textarea rows={1} className="input-dark flex-1 resize-none text-sm"
                  placeholder={isFr ? "Écrivez votre question…" : "Type your question…"}
                  value={aiInput} onChange={(e) => setAiInput(e.target.value)}
                  onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); sendAI(); } }}
                  data-testid="support-ai-input" />
                <button onClick={sendAI} disabled={aiLoading || !aiInput.trim()} className="btn-gold text-xs px-3 py-2 disabled:opacity-50" data-testid="support-ai-send">
                  <Send size={12} />
                </button>
              </div>
            </div>
          )}

          {tab === "ticket" && (
            <form onSubmit={submitTicket} className="p-4 space-y-3">
              {ticketSent ? (
                <div className="text-sm px-3 py-3 rounded-xl" style={{ background: "rgba(74,222,128,0.12)", color: "#4ADE80", border: "1px solid rgba(74,222,128,0.3)" }} data-testid="support-ticket-ok">
                  ✓ {isFr ? "Message envoyé — notre équipe répond sous 24h ouvrables." : "Message sent — our team replies within 24 business hours."}
                </div>
              ) : (
                <>
                  <p className="text-xs" style={{ color: "rgba(245,245,245,0.6)" }}>
                    {isFr ? "Envoyez votre demande à notre équipe." : "Send your request to our team."}
                  </p>
                  <input className="input-dark text-sm"
                    placeholder={isFr ? "Sujet" : "Subject"}
                    value={subject} onChange={(e) => setSubject(e.target.value)}
                    data-testid="support-subject" />
                  <textarea className="input-dark text-sm" rows={4}
                    placeholder={isFr ? "Décrivez votre besoin ou incident…" : "Describe your need or issue…"}
                    value={message} onChange={(e) => setMessage(e.target.value)} required
                    data-testid="support-message" />
                  {ticketErr && (
                    <div className="text-xs px-2 py-1.5 rounded" style={{ background: "rgba(192,0,0,0.12)", color: "#FF6B6B" }}>{ticketErr}</div>
                  )}
                  <button type="submit" disabled={sending} className="btn-gold w-full text-sm disabled:opacity-50" data-testid="support-submit">
                    <Send size={13} /> {sending ? (isFr ? "Envoi…" : "Sending…") : (isFr ? "Envoyer" : "Send")}
                  </button>
                  <p className="text-[10px] text-center" style={{ color: "rgba(245,245,245,0.4)" }}>
                    {isFr ? "Ou écrivez à " : "Or email "}
                    <a href="mailto:support@progere.ca" style={{ color: "#D4AF37" }}>support@progere.ca</a>
                  </p>
                </>
              )}
            </form>
          )}
        </div>
      )}
    </>
  );
}

const TabBtn = ({ active, onClick, children, testid, icon }) => (
  <button onClick={onClick} type="button" data-testid={testid}
    className="flex-1 text-[11px] uppercase tracking-widest font-semibold py-1.5 rounded-lg flex items-center justify-center gap-1.5 transition"
    style={{
      background: active ? "rgba(212,175,55,0.18)" : "transparent",
      color: active ? "#D4AF37" : "rgba(245,245,245,0.55)",
      border: `1px solid ${active ? "rgba(212,175,55,0.35)" : "transparent"}`,
    }}>
    {icon}{children}
  </button>
);
